import React from "react";
import { Fragment } from "react";
import Axios, { AxiosError } from "axios";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { Typography, Box } from "@mui/material";
import Cookies from "js-cookie";
import { ResourceHeaderProps } from "react-big-calendar";

import Title from "../../components/molecules/Title";
import { TextFieldForSearch } from "@/components/molecules/SearchTextField";
import { AutoCompleteSearch } from "@/components/molecules/AutoCompleteSearch";
import { Button } from "@/components/molecules/Button";
import { useAuthUser } from "@/hooks/useAuthUser";
import toast from "@/components/hoc/SnackbarAlert";
import {
  searchChargeUsageStatus,
  SearchChargeUsageStatusValidator,
} from "@/models/chargerUsageStatus";
import { ChargePlan, Palet } from "@/models/chargePlans";
import { Office, PLUS_CITY, ONLY_CONTRACT } from "@/models/office";
import { GX_ADMIN } from "@/models/auth";
import {
  ChargerUsageStatusConstants,
  chargerUsageStatusEventPropGetter,
  EMPTY_EVENT_BODY_COLORS,
  OutputControl,
} from "./ChargerUsageStatusConstants";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { getOfficesAPI, getOutputControlChargersAPI } from "@/util/api/API";
import {
  getChargerUsageStatusAPI,
  cancelOutputControlChargerUsageStatusAPI,
} from "@/util/api/chargePlans";
import { getJSTCurrentTime } from "@/util/datetime";
import { SINGLE_OFFICE_ID, ErrorResponse } from "@/common/CommonConstants";
import { getOfficeOptionLabel } from "@/util";
import {
  GxBigCalendar,
  GxBigCalendarHeader,
  GxBigCalendarResource,
  GXResourceHeaderProps,
} from "@/components/molecules/GxBigCalendar";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";

export const ChargerUsageStatus = (): JSX.Element => {
  const [chargerUsageStatus, setChargerUsageStatus] =
    React.useState<ChargePlan<Palet.Usage>>();
  const [outputControlChargers, setOutputControlChargers] = React.useState<number[]>();
  const [offices, setOffices] = React.useState<Office[]>([]);
  const [officeSelected, setOfficeSelected] = React.useState<Office>();
  const [targetDateSelected, setTargetDateSelected] = React.useState<string>(
    getJSTCurrentTime("date")
  );
  const [searchData, setSearchData] = React.useState<searchChargeUsageStatus>({
    targetDate: getJSTCurrentTime("date"),
  });
  const [searchDataValidator, setSearchDataValidator] =
    React.useState<SearchChargeUsageStatusValidator>({});
  const [initSearchFlag, setInitSearchFlag] = React.useState<boolean>(false);
  const { user } = useAuthUser();
  const [enableOutputControlDate, setEnableOutputControlDate] = React.useState<boolean>();
  const [editing, cancelOutputControlAPIWrapper] = useLoadingPromise(
    cancelOutputControlChargerUsageStatusAPI
  );

  //Get list office to select
  const getOffices = async () => {
    try {
      const res = await getOfficesAPI(
        user?.role === GX_ADMIN ? PLUS_CITY : ONLY_CONTRACT
      );
      setOffices(res.data || []);
      const found = res.data.find(
        (d) => d.id === Number(Cookies.get(SINGLE_OFFICE_ID))
      );
      if (found) {
        setOfficeSelected(found);
        setSearchData({
          ...searchData,
          officeId: found.id,
        });
        setInitSearchFlag(true);
      }
    } catch (err) {
      console.log("==Error when get offices==", err);
    }
  };

  const handleSearchData = React.useCallback(
    async (search: searchChargeUsageStatus) => {
      let haveError = false;
      const searchDataValidatorTemp: SearchChargeUsageStatusValidator = {};
      if (!officeSelected) {
        haveError = true;
        searchDataValidatorTemp.officeId = true;
      }
      if (haveError) {
        setSearchDataValidator({
          ...searchDataValidatorTemp,
        });
        return;
      }

      try {
        const chargerRes = await getOutputControlChargersAPI(
          officeSelected?.id ?? -1
        );
        setOutputControlChargers(chargerRes.data.chargerIds);
      } catch (err) {
        setOutputControlChargers([]);
        console.log("==Error when getOutputControlChargersAPI==", err);
      }

      try {
        const res = await getChargerUsageStatusAPI(search);
        const data: ChargePlan<Palet.Usage> = JSON.parse(
          JSON.stringify(res.data)
        );

        if (!data.chargePlanCalendarResource.length) {
          setChargerUsageStatus(undefined);
          return;
        }

        // 空文字指定のあるイベントのイベントタイトルを空文字に指定する
        data.chargePlanCalendarEvent = data.chargePlanCalendarEvent.map(
          (event) => {
            if (!EMPTY_EVENT_BODY_COLORS.includes(event.color)) {
              return event;
            }

            event.title = "";
            return event;
          }
        );

        for (const event of data.chargePlanCalendarEvent) {
          event.start = new Date(event.start.toString().replace("-", "/"));
          if (event.color === "suspension" && event.end === null) {
            let date = new Date();
            date.setDate(event.start.getDate() + 2);
            event.end = date;
          } else {
            event.end = new Date(event.end.toString().replace("-", "/"));
          }
          // OSSバグ対策
          // startとendどちらも0:00だった場合、表示ができなくなるため
          event.end.setMilliseconds(1);
        }
        console.log(data);
        setChargerUsageStatus(data);
      } catch (err) {
        console.log("==Error when getChargerUsageStatusAPI==", err);
      }
    },
    [officeSelected]
  );

  // Handle event reset search data
  const onClickResetSearchData = () => {
    setOfficeSelected(undefined);
    setSearchData({ targetDate: getJSTCurrentTime("date") });
    setSearchDataValidator({});
    setTargetDateSelected(getJSTCurrentTime("date"));
  };

  const onChangeOffice = React.useCallback(
    (office: Office | undefined) => {
      setSearchData({
        ...searchData,
        officeId: office?.id,
      });
      setSearchDataValidator({
        ...searchDataValidator,
        officeId: false,
      });
      setOfficeSelected(office);
      Cookies.set(SINGLE_OFFICE_ID, office?.id.toString() || "");
    },
    [searchData, searchDataValidator]
  );

  const onChangeChargerName = React.useCallback(
    (name: string) => {
      setSearchData({
        ...searchData,
        chargerNamePart: name,
      });
    },
    [searchData]
  );

  const onChangeDate = React.useCallback(
    (date: string) => {
      setSearchData({
        ...searchData,
        targetDate: date,
      });
      setTargetDateSelected(date);
      handleSearchData({
        ...searchData,
        targetDate: date,
      });
    },
    [searchData, handleSearchData]
  );

  const exeCancelOutputControl = React.useCallback(
    async (chargerId: number) => {
      if (editing) return;
      cancelOutputControlAPIWrapper({ chargerId, targetDate: targetDateSelected })
        .then(() => {
          toast.success(OutputControl.CancelSuccess);
        })
        .catch((e: AxiosError<ErrorResponse>) => {
          if (
            Axios.isAxiosError(e) &&
            e.response &&
            (e.response.status === 404 || e.response.status === 409)
          ) {
            toast.error(
              OutputControl.CancelFailed + "：" + e?.response?.data?.message
            );
          }
          console.log("==Error when exeCancelOutputControl==", e);
        })
        .finally(async () => {
          const chargerRes = await getOutputControlChargersAPI(
            officeSelected?.id ?? -1
          );
          setOutputControlChargers(chargerRes.data.chargerIds);
        });
    },
    [editing, targetDateSelected, cancelOutputControlAPIWrapper, officeSelected]
  );

  // 当日の出力コントロールを解除する
  // 事前に確認ダイアログを表示する
  const handleOutputControl = React.useCallback(
    (chargerId: number, chargerName: string) => {
      const message =
        `充電器: ${chargerName}\n日付: ${new Date(targetDateSelected).getMonth() + 1
        }/${new Date(targetDateSelected).getDate()}\n\n` +
        "この出力コントロールを取り消します。よろしければOKをクリックしてください";
      if (window.confirm(message)) {
        exeCancelOutputControl(chargerId);
      }
    },
    [targetDateSelected, exeCancelOutputControl]
  );

  const components: GxBigCalendarHeader = {
    toolbar: () => null,
    resourceHeader: (props: ResourceHeaderProps) => {
      const { resource } =
        props as GXResourceHeaderProps<GxBigCalendarResource>;

      return (
        <Box>
          <Typography>{resource.headerCode}</Typography>
          {enableOutputControlDate &&
            outputControlChargers?.includes(resource.resourceId) && (
              <Button
                label={OutputControl.Label}
                sx={{
                  backgroundColor: (theme) => theme.palette.error.main,
                  fontSize: "0.8rem",
                  lineHeight: 1.5,
                  padding: 0.5,
                }}
                onClick={() =>
                  handleOutputControl(resource.resourceId, resource.headerCode)
                }
                disabled={editing}
              />
            )}
        </Box>
      );
    },
  } as const;

  // First load
  React.useEffect(() => {
    getOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 現在日のみ出力コントロール可能
  React.useEffect(() => {
    setEnableOutputControlDate(targetDateSelected === getJSTCurrentTime("date"));
  }, [targetDateSelected]);

  // Cookieの営業所IDが営業所リストに存在した場合
  React.useEffect(() => {
    if (initSearchFlag) {
      handleSearchData({ ...searchData });
      setInitSearchFlag(false);
    }
  }, [initSearchFlag, handleSearchData, searchData]);

  return (
    <Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title>{ChargerUsageStatusConstants.UsageStatus}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6} md={6} lg={6.1} className="required">
                  <AutoCompleteSearch
                    options={offices}
                    autoHighlight
                    getOptionLabel={getOfficeOptionLabel}
                    value={officeSelected || undefined}
                    onChange={(e, value) => {
                      onChangeOffice(value || undefined);
                    }}
                    label={ChargerUsageStatusConstants.OfficeName}
                    error={searchDataValidator.officeId}
                  />
                </Grid>
                <Grid item xs={3} md={3} lg={3.1}>
                  <TextFieldForSearch
                    label={ChargerUsageStatusConstants.ChargerName}
                    value={searchData.chargerNamePart || ""}
                    setValue={(value) => onChangeChargerName(value)}
                    sx={{ width: "100%" }}
                  />
                </Grid>
                <Grid item xs={12} xl={2.8} container justifyContent="flex-end">
                  <CommonButton
                    btnType="Search"
                    onClickBtn={() => handleSearchData({ ...searchData })}
                  />
                  <CommonButton
                    btnType="ResetSearchCriteria"
                    onClickBtn={onClickResetSearchData}
                    sx={{ ml: 2 }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={10} md={10} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <GxBigCalendar
                targetDateSelected={targetDateSelected}
                events={chargerUsageStatus?.chargePlanCalendarEvent}
                resources={chargerUsageStatus?.chargePlanCalendarResource}
                onChangeDate={onChangeDate}
                eventPropGetter={chargerUsageStatusEventPropGetter}
                components={components}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </Fragment>
  );
};
